import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Eye, EyeOff, Lock, Mail, AlertCircle, Smartphone, ArrowLeft } from "lucide-react";
import { auth, db } from "../firebase";
import { signInWithEmailAndPassword } from "firebase/auth";
import { ref, get } from "firebase/database";
import { TOTP } from "otpauth";

export default function AdminLogin() {
  const [step, setStep] = useState(1); // 1 = credentials, 2 = 2FA code
  const [email, setEmail] = useState("");
  const [firebasePassword, setFirebasePassword] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [totpCode, setTotpCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  // Store UID between steps
  const pendingUid = useRef(null);

  async function handleLogin(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const cred = await signInWithEmailAndPassword(auth, email, firebasePassword);
      const uid = cred.user.uid;

      // Check UID is in /admins
      const adminSnap = await get(ref(db, `admins/${uid}`));
      if (!adminSnap.exists()) {
        await auth.signOut();
        setError("This account is not authorized as admin.");
        setLoading(false);
        return;
      }

      // Check admin secret (Firebase-stored only)
      let validSecret = null;
      try {
        const secretSnap = await get(ref(db, "adminConfig/secretPassword"));
        if (secretSnap.exists()) validSecret = secretSnap.val();
      } catch (e) { /* could not read adminConfig */ }
      if (!validSecret || adminPassword !== validSecret) {
        await auth.signOut();
        setError("Incorrect admin secret password.");
        setLoading(false);
        return;
      }

      // Check if 2FA is enabled
      let totpEnabled = false;
      try {
        const totpSnap = await get(ref(db, "adminConfig/totpSecret"));
        totpEnabled = totpSnap.exists();
      } catch (e) { /* rules not yet updated — skip 2FA check */ }
      if (totpEnabled) {
        // Store UID for step 2, show TOTP form
        pendingUid.current = uid;
        setStep(2);
        setLoading(false);
        return;
      }

      // No 2FA — grant access directly
      localStorage.setItem("cs_admin", "true");
      localStorage.setItem("cs_admin_uid", uid);
      navigate("/admin");
    } catch (err) {
      const code = err.code || err.message || "unknown";
      if (code === "auth/user-not-found") setError("No account found with this email.");
      else if (code === "auth/wrong-password" || code === "auth/invalid-credential") setError("Incorrect password.");
      else if (code === "auth/invalid-email") setError("Invalid email format.");
      else if (code === "auth/too-many-requests") setError("Too many attempts. Wait a few minutes.");
      else setError(`Login error: ${code}`);
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyTotp(e) {
    e.preventDefault();
    setError("");
    if (totpCode.length !== 6) { setError("Enter the 6-digit code from your authenticator app."); return; }
    setLoading(true);
    try {
      const totpSnap = await get(ref(db, "adminConfig/totpSecret"));
      if (!totpSnap.exists()) { setError("2FA not configured. Contact admin."); setLoading(false); return; }

      const totp = new TOTP({ secret: totpSnap.val(), algorithm: "SHA1", digits: 6, period: 30 });
      const delta = totp.validate({ token: totpCode, window: 1 });

      if (delta === null) {
        setError("Invalid 2FA code. Check your authenticator app and try again.");
        setTotpCode("");
        setLoading(false);
        return;
      }

      // 2FA verified — grant access
      localStorage.setItem("cs_admin", "true");
      localStorage.setItem("cs_admin_uid", pendingUid.current);
      navigate("/admin");
    } catch (err) {
      setError("Verification failed. Please try again.");
    }
    setLoading(false);
  }

  function handleBackToStep1() {
    auth.signOut();
    pendingUid.current = null;
    setStep(1);
    setTotpCode("");
    setError("");
  }

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">

        {/* Header */}
        <div className="text-center mb-8">
          <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl shadow-lg mb-4 ${
            step === 2 ? "bg-gradient-to-br from-purple-600 to-indigo-700 shadow-purple-900/40" : "bg-gradient-to-br from-blue-600 to-indigo-700 shadow-blue-900/40"
          }`}>
            {step === 2 ? <Smartphone size={32} className="text-white" /> : <ShieldCheck size={32} className="text-white" />}
          </div>
          <h1 className="text-2xl font-bold text-white">
            {step === 2 ? "Two-Factor Auth" : "Admin Panel"}
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            {step === 2 ? "Enter the code from your authenticator app" : "CampusSwap Owner Access"}
          </p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <div className={`h-1.5 w-16 rounded-full ${step >= 1 ? "bg-blue-500" : "bg-gray-700"}`} />
          <div className={`h-1.5 w-16 rounded-full ${step >= 2 ? "bg-purple-500" : "bg-gray-700"}`} />
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 shadow-2xl">
          {error && (
            <div className="flex items-center gap-2 bg-red-900/30 border border-red-800/50 text-red-400 rounded-xl px-4 py-3 mb-4 text-sm">
              <AlertCircle size={16} className="flex-shrink-0" />
              {error}
            </div>
          )}

          {/* ── STEP 1: Credentials ── */}
          {step === 1 && (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-gray-400 text-xs font-semibold mb-1.5 uppercase tracking-wider">Account Email</label>
                <div className="relative">
                  <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full bg-gray-800 border border-gray-700 text-white pl-10 pr-4 py-3 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-600"
                    required />
                </div>
              </div>

              <div>
                <label className="block text-gray-400 text-xs font-semibold mb-1.5 uppercase tracking-wider">Account Password</label>
                <div className="relative">
                  <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input type={showPassword ? "text" : "password"} value={firebasePassword} onChange={e => setFirebasePassword(e.target.value)}
                    placeholder="Your login password"
                    className="w-full bg-gray-800 border border-gray-700 text-white pl-10 pr-10 py-3 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-600"
                    required />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-gray-400 text-xs font-semibold mb-1.5 uppercase tracking-wider">Admin Secret</label>
                <div className="relative">
                  <ShieldCheck size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input type="password" value={adminPassword} onChange={e => setAdminPassword(e.target.value)}
                    placeholder="Admin secret password"
                    className="w-full bg-gray-800 border border-gray-700 text-white pl-10 pr-4 py-3 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-600"
                    required />
                </div>
              </div>

              <button type="submit" disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white py-3 rounded-xl font-semibold text-sm transition-all shadow-lg shadow-blue-900/30 disabled:opacity-50">
                {loading ? "Verifying..." : "Continue →"}
              </button>
            </form>
          )}

          {/* ── STEP 2: TOTP Code ── */}
          {step === 2 && (
            <form onSubmit={handleVerifyTotp} className="space-y-4">
              <div className="bg-purple-900/20 border border-purple-800/40 rounded-xl p-3 text-sm text-purple-300 flex items-start gap-2">
                <Smartphone size={16} className="flex-shrink-0 mt-0.5" />
                <span>Open your authenticator app (Google Authenticator / Authy) and enter the 6-digit code for <strong>CampusSwap Admin</strong>.</span>
              </div>

              <div>
                <label className="block text-gray-400 text-xs font-semibold mb-1.5 uppercase tracking-wider">Authentication Code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={totpCode}
                  onChange={e => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="_ _ _ _ _ _"
                  className="w-full bg-gray-800 border border-gray-700 text-white py-4 px-4 rounded-xl text-center text-2xl tracking-[0.8em] font-bold focus:outline-none focus:ring-2 focus:ring-purple-500 placeholder-gray-700"
                  autoFocus
                  required
                />
                <p className="text-gray-600 text-xs text-center mt-2">Code refreshes every 30 seconds</p>
              </div>

              <button type="submit" disabled={loading || totpCode.length !== 6}
                className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white py-3 rounded-xl font-semibold text-sm transition-all shadow-lg disabled:opacity-50">
                {loading ? "Verifying..." : "Verify & Login"}
              </button>

              <button type="button" onClick={handleBackToStep1}
                className="w-full flex items-center justify-center gap-1.5 text-gray-500 hover:text-gray-300 text-xs py-2 transition-colors">
                <ArrowLeft size={13} /> Back to login
              </button>
            </form>
          )}

          <p className="text-center text-gray-700 text-xs mt-5">
            Unauthorized access attempts are logged.
          </p>
        </div>

        <p className="text-center text-gray-700 text-xs mt-5">
          CampusSwap Admin v2.0 · Authorized Access Only
        </p>
      </div>
    </div>
  );
}
